import { useMemo } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import type {
  BillingProviderProps,
  TimeEntryUpdate,
  ViewInvoicesWithDetails,
  ViewTimeEntriesWithProfiles,
} from '@/types'
import { BillingContext } from '../hooks/useBilling'

export const BillingProvider = ({
  children,
  caseId,
}: BillingProviderProps): JSX.Element => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Fetch time entries query
  const {
    data: timeEntries = [],
    isLoading: isLoadingTimeEntries,
    refetch: refetchTimeEntries,
  } = useQuery({
    queryKey: ['time-entries', caseId],
    queryFn: async (): Promise<ViewTimeEntriesWithProfiles[]> => {
      if (!user) return []

      let query = supabase
        .from('view_time_entries_with_profiles')
        .select('*')
        .order('entry_date', { ascending: false })

      if (caseId) {
        query = query.eq('case_id', caseId)
      }
      const { data, error } = await query

      if (error) throw error

      return data
    },
    enabled: Boolean(user),
  })

  // Fetch invoices query
  const {
    data: invoices = [],
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ['invoices'],
    queryFn: async (): Promise<ViewInvoicesWithDetails[]> => {
      if (!user) return []

      const { data, error } = await supabase
        .from('view_invoices_with_details')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      return data
    },
    enabled: Boolean(user),
  })

  // Add time entry mutation
  const addTimeEntryMutation = useMutation({
    mutationFn: async (entry: TimeEntryUpdate): Promise<unknown> => {
      if (!user) throw new Error('Auth required')

      const { data: professional } = await supabase
        .from('professionals')
        .select('id, chamber_id')
        .eq('user_id', user.id)
        .single()

      if (!professional?.chamber_id) throw new Error('Not part of a chamber')

      const { data, error } = await supabase
        .from('time_entries')
        .update(entry)
        .select()
        .single()

      if (error) throw error

      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time-entries', caseId] })
      toast.success('Time entry updated successfully')
    },
    onError: (error) => {
      console.error('Error adding time entry:', error)
      toast.error('Failed to update time entry')
    },
  })

  const value = useMemo(() => {
    return {
      timeEntries,
      invoices,
      isLoadingTimeEntries,
      isLoadingInvoices,
      refetchTimeEntries,
      refetchInvoices,
      addTimeEntry: addTimeEntryMutation.mutateAsync,
      isAddingTimeEntry: addTimeEntryMutation.isPending,
    }
  }, [
    timeEntries,
    invoices,
    isLoadingTimeEntries,
    isLoadingInvoices,
    refetchTimeEntries,
    refetchInvoices,
    addTimeEntryMutation.mutateAsync,
    addTimeEntryMutation.isPending,
  ])

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  )
}
